/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/ActionCreatePage';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { analyzeActionDelete } from './ActionDeleteAnalyze';
import { defaultValue } from './ActionDeleteCurrentRecord';

const ActionDeleteCurrentRecordManager: ActionManagerFn<
  'ActionDeleteCurrentRecord'
> = (platform: Platform): ActionManager<'ActionDeleteCurrentRecord'> => {
  return {
    ...baseActionManager(platform, 'ActionDeleteCurrentRecord'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['DataStore', 'LocalDataStore', 'TransientDataStore'],
      },
      confirmationTitle: {
        label: 'Confirmation Dialog Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      confirmationMessage: {
        label: 'Confirmation Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Leave blank to skip the confirmation dialog',
      },
    },
    name: 'Mark Current Record for Deletion',
    getDisplayText: (item) => item.props.alias,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'trash' },
    analyze: analyzeActionDelete,
    description:
      'Use this action to mark a record in a Data Store for Deletion. To post it to the database, use Action Save or Save Transaction after marking one or more records for deletion.',
    helpRenders: [
      {
        title: 'Action Mark Current Record for Deletion',
        description: `Select a record from the below grid and press the Invoke Action button to see it in action.
            
\`\`\`alert
Note: This action will just mark the record for deletion, you need to use Action Save or Save Trasaction to persist to the database.
\`\`\``,
        page: SamplePage,
        height: 300,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: [],
        item: {
          children: [],
          itemId: 'actionDelete',
          parent: 'triggerOnClick',
          props: {
            alias: 'test',
          },
          type: 'ActionDeleteCurrentRecord',
        },
      },
      {
        title: 'User Confirmation',
        description:
          'If the Confirmation Message prop is set with some value, then a user confirmation dialog will be shown confirming the delete, given user an option to cancel the action.',
        page: SamplePage,
        height: 300,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['confirmationTitle', 'confirmationMessage'],
        item: {
          children: [],
          itemId: 'actionDelete',
          parent: 'triggerOnClick',
          props: {
            alias: 'test',
            confirmationMessage: { type: 'text', value: 'Are you sure?' },
          },
          type: 'ActionDeleteCurrentRecord',
        },
      },
      {
        title: 'Dynamic Confirmation Message',
        description:
          'Use expression to show exactly what the user is trying to delete.',
        page: SamplePage,
        height: 300,
        exampleId: 'e3',
        clearParentChildren: true,
        visibleProps: ['confirmationTitle', 'confirmationMessage'],
        item: {
          children: [],
          itemId: 'actionDelete',
          parent: 'triggerOnClick',
          props: {
            alias: 'test',
            confirmationTitle: {
              type: 'expression',
              // eslint-disable-next-line no-template-curly-in-string
              value: '`Delete ${$.test.name}?`',
            },
            confirmationMessage: {
              type: 'expression',
              // eslint-disable-next-line no-template-curly-in-string
              value: '`Are you sure, you want to delete ${$.test.name}?`',
            },
          },
          type: 'ActionDeleteCurrentRecord',
        },
      },
    ],
  };
};

export default ActionDeleteCurrentRecordManager;
